import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  Inject,
  OnInit,
  Signal,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  MatOption,
  MatSelect,
  MatSelectChange,
  MatSelectTrigger,
} from '@angular/material/select';
import { allEventVariables } from '@consts/alerts-v2.const';
import { WorkflowModel } from '@models/workflow.model';
import { TranslatePipe } from '@ngx-translate/core';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { ValidatorsHelpers } from 'src/app/helpers/validators.helpers';
import { ParamsTableComponent } from 'src/app/pages/settings/pages/oauth-settings/components/params-table/params-table.component';
import {
  authTypesMap,
  HttpMethodsMap,
  WebhookTabs,
} from 'src/app/pages/workflow/node-edit.type';
import { OptionsTableComponent } from '../options-table/options-table.component';
import { Store } from '@ngrx/store';
import { WorkflowWebhookModel } from '@models/workflow-webhook.model';
import { WorkflowWebhookActions } from '@states/workflow-webhooks/workflow-webhooks.action-type';
import { ActiveOrganization } from '@models/organization.model';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { WorkflowWebhooksSelectors } from '@states/workflow-webhooks/workflow-webhooks.selector-types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EnableEscCloseDirective, UiButtonComponent, UiCopyClipboardComponent } from '@ui';
import { Dictionary } from '@ngrx/entity/src';

export interface WebhookDialogData {
  webhookId: string;
}

export interface WorkflowForm {
  name: string;
  method: WorkflowModel.HttpMethods;
  address: string;
  queryParams: {
    key: string;
    value: string;
  }[];
  auth: WorkflowWebhookModel.WebhookAuthType;
  credential: {
    username?: string;
    password?: string;
    token?: string;
  };
  body: string;
  allowOrigins: {
    value: string;
  }[];
  allowIps: {
    value: string;
  }[];
}

@Component({
  selector: 'app-webhook-dialog',
  imports: [
    MatDialogClose,
    TranslatePipe,
    UiButtonComponent,
    ParamsTableComponent,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    MatSelectTrigger,
    MonacoEditorModule,
    OptionsTableComponent,
    UiCopyClipboardComponent,
  ],
  templateUrl: './webhook-dialog.component.html',
  styleUrl: './webhook-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [EnableEscCloseDirective],
})
export class WebhookDialogComponent implements OnInit {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: WebhookDialogData,
    private dialogRef: MatDialogRef<WebhookDialogComponent | null>,
    private fb: UntypedFormBuilder,
    private store$: Store,
    private destroyRef: DestroyRef,
  ) {
    effect(() => {
      const orgId = this.selectOrganizationSig().orgId as string;

      if (!Object.keys(this.selectWebhooksEntitiesSig() ?? {}).length && orgId) {
        this.store$.dispatch(WorkflowWebhookActions.getWorkflowWebhooks({ orgId }))
      }

      if (this.webhookSig()) {
        const webhook = this.selectWebhooksEntitiesSig()[this.data.webhookId];
        this.form.controls['name'].setValue(webhook.name);
        this.form.controls['method'].setValue(webhook.config.method);
        this.changeAuthForm(webhook.config.authType);
        this.form.controls['credential'].setValue(webhook.config.credential);

        if (webhook.config.options?.allowIps?.length) {
          const allowOrigins = webhook.config.options.allowOrigins.map(
            (value) => ({ value }),
          );
          this.form.controls['allowOrigins'].setValue(allowOrigins);
        }
        if (webhook.config.options?.allowIps?.length) {
          const allowIps = webhook.config.options.allowIps.map((value) => ({
            value,
          }));
          this.form.controls['allowIps'].setValue(allowIps);
        }

      }
    })
  }

  public selectOrganizationSig: Signal<ActiveOrganization> =
    this.store$.selectSignal(OrganizationSelectors.selectActiveOrganization);
  public selectIsSavingSig: Signal<boolean> = this.store$.selectSignal(
    WorkflowWebhooksSelectors.selectIsSaving,
  );
  public selectWebhooksEntitiesSig: Signal<
    Dictionary<WorkflowWebhookModel.WebhookEndpointMongoDocument>
  > = this.store$.selectSignal(
    WorkflowWebhooksSelectors.selectWorkflowWebhooksEntities,
  );

  public selectCreatedId$ = this.store$.select(
    WorkflowWebhooksSelectors.selectCreatedId,
  );

  public webhookSig = computed(() => {
    if (Object.keys(this.selectWebhooksEntitiesSig() ?? {}).length && this.data?.webhookId) {
      const webhook = this.selectWebhooksEntitiesSig()[this.data.webhookId];

      return webhook;
    }

    return undefined;
  })

  public readonly httpMethodsMap = HttpMethodsMap;
  public readonly httpOptions = Object.keys(HttpMethodsMap);
  public readonly httpMethods = WorkflowModel.HttpMethods;
  public readonly webhookTabs = WebhookTabs;
  public readonly authTypes = WorkflowWebhookModel.WebhookAuthType;
  public readonly authTypesOptions = Object.keys(authTypesMap);
  public readonly authTypesMap = authTypesMap;
  protected editorOptions = signal({ language: 'json', automaticLayout: true });

  public form = this.fb.group({
    name: ['', [Validators.required]],
    method: [WorkflowModel.HttpMethods.POST],
    queryParams: this.fb.array([]),
    auth: [WorkflowWebhookModel.WebhookAuthType.NONE],
    credential: this.fb.group({}),
    body: ['', [ValidatorsHelpers.jsonValidator]],
    allowOrigins: this.fb.array([]),
    allowIps: this.fb.array([]),
  });

  public selectedTabSig = signal(WebhookTabs.QueryParams);

  public ngOnInit(): void {
    const queryParams = this.form.get('queryParams') as FormArray;
    queryParams.push(
      this.fb.group({
        key: [''],
        value: [''],
      }),
    );

    const allowOrigins = this.form.get('allowOrigins') as FormArray;
    allowOrigins.push(
      this.fb.group({
        value: [''],
      }),
    );

    const allowIps = this.form.get('allowIps') as FormArray;
    allowIps.push(
      this.fb.group({
        value: [''],
      }),
    );
  }

  public close(): void {
    this.dialogRef.close();
  }

  public save(): void {
    const formValue = this.form.value as WorkflowForm;

    const dto: WorkflowWebhookModel.CreateWebhookDto = {
      name: formValue.name,
      target: {
        targetType: WorkflowWebhookModel.WebhookRouteTargetType.emptyTarget,
      },
      config: {
        authType: formValue.auth,
        method: formValue.method,
        credential: { ...formValue.credential },
        options: {
          ...(formValue.allowOrigins[0].value
            ? {
              allowOrigins: formValue.allowOrigins.map(
                (item: { value: string }) => item.value,
              ),
            }
            : {}),
          ...(formValue.allowIps[0].value
            ? {
              allowIps: formValue.allowIps.map(
                (item: { value: string }) => item.value,
              ),
            }
            : {}),
        },
      },
    };

    const orgId = this.selectOrganizationSig().orgId as string;

    if (this.data?.webhookId) {
      this.store$.dispatch(
        WorkflowWebhookActions.editWorkflowWebhook({
          id: this.data.webhookId,
          webhook: dto,
        }),
      );
    } else {
      this.store$.dispatch(
        WorkflowWebhookActions.createWorkflowWebhook({
          orgId: orgId,
          webhook: dto,
        }),
      );
    }

    this.selectCreatedId$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((id) => {
        if (id) {
          this.dialogRef.close({ webhookId: id });
        }
      });
  }

  public changeAuthFormOnSelect(ev: MatSelectChange): void {
    this.changeAuthForm(ev.value as WorkflowWebhookModel.WebhookAuthType);
  }

  public changeAuthForm(authType: WorkflowWebhookModel.WebhookAuthType): void {
    let credentialFormGroup: UntypedFormGroup;
    switch (authType) {
      case WorkflowWebhookModel.WebhookAuthType.BASIC:
        credentialFormGroup = this.fb.group({
          username: ['', Validators.required],
          password: ['', Validators.required],
        });
        break;
      case WorkflowWebhookModel.WebhookAuthType.JWT:
        credentialFormGroup = this.fb.group({
          token: ['', Validators.required],
        });
        break;
      default:
        credentialFormGroup = this.fb.group({});
    }
    this.form.controls['auth'].setValue(authType);
    this.form.removeControl('credential');
    this.form.addControl('credential', credentialFormGroup);
  }

  public onEditorInit(editor: any): void {
    const monaco = (window as any).monaco;
    setTimeout(() => {
      editor.getAction('editor.action.formatDocument')?.run();
    }, 500);

    if (!(window as any)?.jsonCompletionProviderRegistered) {
      monaco.languages.registerCompletionItemProvider('json', {
        provideCompletionItems: async (model, position) => {
          const textBeforeCursor = model.getValueInRange({
            startLineNumber: position.lineNumber,
            startColumn: 1,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          });

          if (!textBeforeCursor.endsWith('@')) {
            return { suggestions: [] };
          }

          const autoComplete = allEventVariables.map((variable) => {
            return {
              label: `@${variable}`,
              kind: monaco.languages.CompletionItemKind.Variable,
              insertText: `"{{${variable}}}"`,
            };
          });

          return {
            suggestions: autoComplete,
          };
        },
      });

      (window as any).jsonCompletionProviderRegistered = true;
    }
  }

  public get getQueryParamsControls(): AbstractControl[] {
    return (this.form.get('queryParams') as FormArray).controls;
  }

  public get getAllowOriginsControls(): AbstractControl[] {
    return (this.form.get('allowOrigins') as FormArray).controls;
  }

  public get getAllowIpsControls(): AbstractControl[] {
    return (this.form.get('allowIps') as FormArray).controls;
  }

  public deleteQueryParams(idx: number): void {
    const target = this.form.get('queryParams') as FormArray;
    this.deleteParam(target, idx);
  }

  public addQueryParams(): void {
    const target = this.form.get('queryParams') as FormArray;
    this.addParam(target);
  }

  public deleteAllowOrigins(idx: number): void {
    const target = this.form.get('allowOrigins') as FormArray;
    this.deleteParam(target, idx);
  }

  public addAllowOrigins(): void {
    const target = this.form.get('allowOrigins') as FormArray;
    this.addParamWithoutKey(target);
  }

  public deleteAllowIps(idx: number): void {
    const target = this.form.get('allowIps') as FormArray;
    this.deleteParam(target, idx);
  }

  public addAllowIps(): void {
    const target = this.form.get('allowIps') as FormArray;
    this.addParamWithoutKey(target);
  }

  private addParam(target: FormArray): void {
    const control = this.fb.group({
      key: [''],
      value: [''],
    });
    target.push(control);
  }

  private addParamWithoutKey(target: FormArray): void {
    const control = this.fb.group({
      value: [''],
    });
    target.push(control);
  }

  private deleteParam(target: FormArray, idx: number): void {
    target.removeAt(idx);
  }
}
